import { createContext, useContext, useState, useCallback } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useLang } from "../i18n";
import { api, formatApiError } from "../lib/api";

const EnquiryContext = createContext();
export const useEnquiry = () => useContext(EnquiryContext);

export function EnquiryProvider({ children }) {
  const [dress, setDress] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [consentChecked, setConsentChecked] = useState(false);
  const [status, setStatus] = useState("idle");
  const { t, lang } = useLang();

  const openEnquiry = useCallback((d = null) => {
    setDress(d);
    setStatus("idle");
  }, []);
  const close = () => setDress(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !consentChecked) return setStatus("error");
    setStatus("sending");
    try {
      await api.post("/inquiries", {
        type: "enquiry", ...form, language: lang,
        dress_id: dress?.id || null, dress_name: dress?.name || null,
      });
      setStatus("sent");
    } catch (err) {
      formatApiError(err.response?.data?.detail);
      setStatus("error");
    }
  };

  return (
    <EnquiryContext.Provider value={{ openEnquiry }}>
      {children}
      <AnimatePresence>
        {dress !== null && (
          <motion.div className="fixed inset-0 z-[100] flex justify-end" data-testid="enquiry-drawer">
            <motion.div className="absolute inset-0 bg-charcoal/45 backdrop-blur-sm"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={close} />
            <motion.div className="relative bg-ivory w-full max-w-md h-full overflow-y-auto p-8 sm:p-12"
              initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
              transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}>
              <button onClick={close} data-testid="enquiry-close" className="eyebrow hover:text-gold transition-colors">✕ &nbsp;Close</button>
              <h3 className="font-serif text-3xl mt-8 mb-2">{t("enquiry.title")}</h3>
              {dress && (
                <p className="eyebrow mb-8">{dress.name} · {dress.code}</p>
              )}
              {status === "sent" ? (
                <p className="font-serif text-xl mt-10 text-gold" data-testid="enquiry-success">{t("enquiry.sent")}</p>
              ) : (
                <form onSubmit={submit} className="mt-8 space-y-6">
                  <input className="field" data-testid="enquiry-name" placeholder={t("enquiry.name")} value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })} />
                  <input className="field" data-testid="enquiry-email" type="email" placeholder={t("enquiry.email")} value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })} />
                  <input className="field" data-testid="enquiry-phone" placeholder={t("enquiry.phone")} value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                  <textarea className="field" data-testid="enquiry-message" rows="4" placeholder={t("enquiry.message")} value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })} />
                  {status === "error" && <p className="text-sm text-red-700">{t("appointments.required")}</p>}
                  <label className="flex items-start gap-3 text-sm text-stone cursor-pointer" data-testid="enquiry-consent">
                    <input type="checkbox" className="mt-1 accent-[#C5A880]" checked={consentChecked}
                      onChange={(e) => setConsentChecked(e.target.checked)} data-testid="enquiry-consent-checkbox" />
                    <span>{t("appointments.consent")}</span>
                  </label>
                  <button className="btn-solid w-full" data-testid="enquiry-submit" disabled={status === "sending" || !consentChecked}>
                    {status === "sending" ? "…" : t("enquiry.send")}
                  </button>
                </form>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </EnquiryContext.Provider>
  );
}
